import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import RecipientCard from "./components/RecipientCard";
import AllocationSummary from "./components/AllocationSummary";
import "../../style/bannerpage.css";
import {
  RECIPIENT_TYPES,
  SELECTABLE_RECIPIENT_TYPES,
  DISCOUNT_OPTIONS,
} from "./constants/recipientTypes";
import { validateRecipients } from "./helpers/validation";
import { dummyRegistration } from "./fixtures/dummyRegistration";

// Payment Allocation Simulator / Allocator.
// - Standalone (tanpa prop registration): mode preview memakai dummy.
// - Integrasi (prop registration + onConfirm): dipakai di overlay ApprovalPage
//   dengan data registrasi asli. `embedded` = render tanpa chrome halaman.
const PaymentAllocationSimulator = ({
  setLocationAdmin,
  registration,
  onConfirm,
  onClose,
  initialDiscount = 0,
  embedded = false,
  submitting = false,
}) => {
  const navigate = useNavigate();
  const isIntegration = typeof onConfirm === "function";
  const activeRegistration = registration || dummyRegistration;
  // Installment ditentukan dari registrasi (bukan per-recipient).
  const isInstallmentPlan = activeRegistration.paymentPlan === "installment";

  const [discount, setDiscount] = useState(initialDiscount);
  const [recipients, setRecipients] = useState([
    { type: RECIPIENT_TYPES.CEA, percent: initialDiscount },
    { type: RECIPIENT_TYPES.PIC, percent: 100 - initialDiscount },
  ]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const debounceRef = useRef(null);

  useEffect(() => {
    if (setLocationAdmin) setLocationAdmin({ pathname: "/payment-allocation-simulator" });
  }, [setLocationAdmin]);

  const studentNames = useMemo(
    () => (activeRegistration.students || []).map((s) => s.name).filter(Boolean),
    [activeRegistration],
  );
  const usedTypes = recipients.map((r) => r.type);

  // CEA selalu mengikuti discount (readonly).
  useEffect(() => {
    setRecipients((prev) =>
      prev.map((r) => (r.type === RECIPIENT_TYPES.CEA ? { ...r, percent: discount } : r)),
    );
  }, [discount]);

  const validation = validateRecipients(recipients);

  // Panggil backend (source of truth) setiap ada perubahan — realtime, debounced.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const payload = {
          registration: activeRegistration,
          discountPercent: discount,
          recipients: recipients.map((r) => ({
            type: r.type,
            percent: r.percent,
            email: r.email,
            // Installment mengikuti payment plan registrasi, bukan per-recipient.
            installment: r.type !== RECIPIENT_TYPES.CEA && isInstallmentPlan,
          })),
        };
        const res = await axios.post("/simulate-allocation", payload);
        setResult(res.data);
      } catch (error) {
        if (error.response?.status === 401) {
          Swal.fire({
            title: "Session Expired",
            text: "Please login to your account!",
            icon: "warning",
            background: "white",
            color: "black",
            confirmButtonColor: "#fb3104",
          });
          navigate("/loginAdmin");
          return;
        }
        setResult(null);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [activeRegistration, discount, recipients, isInstallmentPlan, navigate]);

  const handleRecipientChange = (index, patch) => {
    setRecipients((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const handleRemoveRecipient = (index) => {
    setRecipients((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddRecipient = () => {
    const nextType = SELECTABLE_RECIPIENT_TYPES.find((t) => !usedTypes.includes(t));
    if (!nextType) return;
    setRecipients((prev) => [...prev, { type: nextType, percent: 0 }]);
  };

  const canAddMore = SELECTABLE_RECIPIENT_TYPES.some((t) => !usedTypes.includes(t));

  const handleConfirm = () => {
    if (!validation.valid || !isIntegration) return;
    onConfirm({
      discountPercent: discount,
      recipients: recipients.map((r) => ({ type: r.type, percent: r.percent, email: r.email })),
    });
  };

  const card = {
    background: "#fff",
    borderRadius: "14px",
    padding: "22px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
  };

  const inner = (
    <>
      {!embedded && (
        <div style={{ marginBottom: "6px" }}>
          <h4 style={{ fontWeight: 800, color: "#231f20", margin: 0 }}>Payment Allocation Simulator</h4>
          <p style={{ fontSize: "13px", color: "#888", margin: "4px 0 0" }}>
            Standalone preview module · uses live pricing &amp; installment logic · does not send anything.
          </p>
        </div>
      )}

      {/* Registration notice */}
      <div style={{ background: "#fff8f7", border: "1px solid #fbd5cc", borderRadius: "10px", padding: "12px 16px", margin: embedded ? "0 0 20px" : "16px 0 24px", fontSize: "13px", color: "#8a4a3c" }}>
        <strong>{isIntegration ? "Registration:" : "Preview data:"}</strong> {activeRegistration.churchName} · PIC {activeRegistration.picName} ({activeRegistration.picEmail}) ·{" "}
        {(activeRegistration.students || []).length} student(s) · plan: {isInstallmentPlan ? "Installment" : "Full Payment"}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.15fr) minmax(0, 1fr)", gap: "22px", alignItems: "start" }} className="pas-grid">
        {/* LEFT: controls */}
        <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
          {/* Discount */}
          <div style={card}>
            <label style={{ fontSize: "13px", fontWeight: 700, color: "#231f20", display: "block", marginBottom: "10px" }}>
              Discount — portion covered by CEA
            </label>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {DISCOUNT_OPTIONS.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDiscount(d)}
                  style={{
                    flex: "1 1 60px",
                    padding: "9px 0",
                    borderRadius: "8px",
                    border: discount === d ? "2px solid #fb3104" : "1px solid #ddd",
                    background: discount === d ? "#fff0ee" : "#fff",
                    color: discount === d ? "#fb3104" : "#555",
                    fontWeight: 700,
                    fontSize: "14px",
                    cursor: "pointer",
                  }}
                >
                  {d}%
                </button>
              ))}
            </div>
            <p style={{ fontSize: "12px", color: "#999", margin: "10px 0 0" }}>
              CEA recipient percentage is locked to this discount.
            </p>
          </div>

          {/* Recipients */}
          <div style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
              <span style={{ fontSize: "13px", fontWeight: 700, color: "#231f20" }}>Recipients</span>
              <button
                type="button"
                onClick={handleAddRecipient}
                disabled={!canAddMore}
                style={{
                  border: "1px dashed #fb3104",
                  background: "#fff",
                  color: canAddMore ? "#fb3104" : "#ccc",
                  borderColor: canAddMore ? "#fb3104" : "#ddd",
                  borderRadius: "8px",
                  padding: "6px 14px",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: canAddMore ? "pointer" : "not-allowed",
                }}
              >
                + Add Recipient
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              {recipients.map((r, i) => (
                <RecipientCard
                  key={i}
                  recipient={r}
                  index={i}
                  usedTypes={usedTypes}
                  picEmail={activeRegistration.picEmail}
                  studentNames={studentNames}
                  onChange={handleRecipientChange}
                  onRemove={handleRemoveRecipient}
                />
              ))}
            </div>

            {/* Validation errors */}
            {!validation.valid && (
              <div style={{ marginTop: "14px" }}>
                {validation.errors.map((e, i) => (
                  <p key={i} style={{ margin: "2px 0", fontSize: "12.5px", color: "#c0392b" }}>• {e}</p>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: summary */}
        <div style={{ ...card, position: "sticky", top: "20px" }}>
          <h6 style={{ fontWeight: 800, color: "#231f20", marginBottom: "10px" }}>Summary</h6>
          <AllocationSummary result={result} loading={loading} />

          {isIntegration ? (
            <>
              <button
                type="button"
                disabled={!validation.valid || submitting}
                onClick={handleConfirm}
                style={{
                  width: "100%",
                  marginTop: "18px",
                  padding: "12px",
                  borderRadius: "10px",
                  border: "none",
                  background: validation.valid && !submitting ? "#fb3104" : "#e2e2e2",
                  color: validation.valid && !submitting ? "#fff" : "#999",
                  fontWeight: 700,
                  fontSize: "15px",
                  cursor: validation.valid && !submitting ? "pointer" : "not-allowed",
                }}
              >
                {submitting ? "Processing…" : "Confirm & Send Payment Links"}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                style={{
                  width: "100%",
                  marginTop: "10px",
                  padding: "10px",
                  borderRadius: "10px",
                  border: "1px solid #ddd",
                  background: "#fff",
                  color: "#555",
                  fontWeight: 600,
                  fontSize: "14px",
                  cursor: submitting ? "not-allowed" : "pointer",
                }}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              disabled={!validation.valid}
              onClick={() =>
                Swal.fire({
                  title: "Simulation valid",
                  text: "Allocation totals 100%. Ready to integrate into Approval.",
                  icon: "success",
                  background: "white",
                  color: "black",
                  confirmButtonColor: "#fb3104",
                })
              }
              style={{
                width: "100%",
                marginTop: "18px",
                padding: "12px",
                borderRadius: "10px",
                border: "none",
                background: validation.valid ? "#fb3104" : "#e2e2e2",
                color: validation.valid ? "#fff" : "#999",
                fontWeight: 700,
                fontSize: "15px",
                cursor: validation.valid ? "pointer" : "not-allowed",
              }}
            >
              Simulate
            </button>
          )}
          {!validation.valid && (
            <p style={{ textAlign: "center", fontSize: "12px", color: "#c0392b", margin: "8px 0 0" }}>
              Total percentage must equal 100%.
            </p>
          )}
        </div>
      </div>

      <style>{`
        @media (max-width: 767.98px) {
          .pas-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </>
  );

  if (embedded) return <div>{inner}</div>;

  return (
    <div style={{ backgroundColor: "#F4F4F4", minHeight: "100vh" }}>
      <div className="custom-con-content-bannerpage">{inner}</div>
    </div>
  );
};

export default PaymentAllocationSimulator;
